"use client"

import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app"
import { getAuth, GoogleAuthProvider, type Auth } from "firebase/auth"
import { getMessaging, type Messaging } from "firebase/messaging"

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
}

const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY

export function isFirebaseConfigured(): boolean {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  )
}

export function isFirebaseMessagingConfigured(): boolean {
  return isFirebaseConfigured() && !!vapidKey?.trim()
}

export function getFirebaseVapidKey(): string {
  if (!vapidKey?.trim()) {
    throw new Error(
      "Firebase messaging is not configured. Set NEXT_PUBLIC_FIREBASE_VAPID_KEY.",
    )
  }
  return vapidKey.trim()
}

function getFirebaseApp(): FirebaseApp {
  if (!isFirebaseConfigured()) {
    throw new Error(
      "Firebase is not configured. Set NEXT_PUBLIC_FIREBASE_* environment variables.",
    )
  }

  if (getApps().length > 0) {
    return getApp()
  }

  return initializeApp(firebaseConfig)
}

export function getFirebaseAuth(): Auth {
  return getAuth(getFirebaseApp())
}

export const googleAuthProvider = new GoogleAuthProvider()

let messagingInstance: Messaging | null = null

export function getFirebaseMessaging(): Messaging {
  if (!isFirebaseMessagingConfigured()) {
    throw new Error(
      "Firebase messaging is not configured. Set NEXT_PUBLIC_FIREBASE_VAPID_KEY.",
    )
  }
  if (typeof window === "undefined") {
    throw new Error("Firebase messaging is only available in the browser.")
  }
  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp())
  }
  return messagingInstance
}

/** Public Firebase config for the messaging service worker. */
export function getFirebaseWebConfig() {
  return { ...firebaseConfig }
}
